import type { VideoEmbedConfig, MapEmbedConfig, CodeEmbedConfig, HtmlEmbedData } from './types';

export function hasValidContent(data: Partial<HtmlEmbedData>): boolean {
  if (!data.embedType) return false;

  switch (data.embedType) {
    case 'video':
      return !!data.video?.videoId;
    case 'map':
      return !!(data.map?.location || data.map?.coordinates);
    case 'social':
      return !!(data.social?.postUrl || data.social?.postId);
    case 'code':
      return !!data.code?.embedId;
    case 'iframe':
      return !!data.iframe?.src;
    case 'custom':
      return !!data.custom?.html;
    default:
      return false;
  }
}

export function getVideoEmbedUrl(config: VideoEmbedConfig): string {
  const params = new URLSearchParams();

  switch (config.platform) {
    case 'youtube':
      if (config.autoplay) params.append('autoplay', '1');
      if (config.muted) params.append('mute', '1');
      if (config.controls !== undefined) params.append('controls', config.controls ? '1' : '0');
      if (config.loop) params.append('loop', '1');
      if (config.startTime) params.append('start', config.startTime.toString());
      return `https://www.youtube.com/embed/${config.videoId}?${params.toString()}`;

    case 'vimeo':
      if (config.autoplay) params.append('autoplay', '1');
      if (config.muted) params.append('muted', '1');
      if (config.loop) params.append('loop', '1');
      if (config.startTime) params.append('t', config.startTime + 's');
      return `https://player.vimeo.com/video/${config.videoId}?${params.toString()}`;

    case 'dailymotion':
      if (config.autoplay) params.append('autoplay', '1');
      if (config.muted) params.append('mute', '1');
      if (config.startTime) params.append('start', config.startTime.toString());
      return `https://www.dailymotion.com/embed/video/${config.videoId}?${params.toString()}`;

    case 'twitch':
      params.append('parent', window.location.hostname);
      if (config.autoplay) params.append('autoplay', 'true');
      if (config.muted) params.append('muted', 'true');
      return `https://player.twitch.tv/?video=${config.videoId}&${params.toString()}`;

    default:
      return '';
  }
}

export function getMapEmbedUrl(config: MapEmbedConfig): string {
  switch (config.provider) {
    case 'google': {
      if (!config.apiKey) return '';
      const googleParams = new URLSearchParams();
      googleParams.append('key', config.apiKey);

      if (config.location) {
        googleParams.append('q', config.location);
      } else if (config.coordinates) {
        googleParams.append('center', `${config.coordinates.lat},${config.coordinates.lng}`);
        if (config.coordinates.zoom) {
          googleParams.append('zoom', config.coordinates.zoom.toString());
        }
      }

      if (config.style) googleParams.append('maptype', config.style);
      return `https://www.google.com/maps/embed/v1/place?${googleParams.toString()}`;
    }

    case 'openstreetmap':
      if (config.coordinates) {
        const { lat, lng } = config.coordinates;
        return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`;
      }
      return '';

    case 'mapbox':
      // Mapbox requires custom implementation
      return '';

    default:
      return '';
  }
}

export function getCodeEmbedUrl(config: CodeEmbedConfig): string {
  switch (config.platform) {
    case 'codepen': {
      const codepenParams = new URLSearchParams();
      if (config.defaultTab) codepenParams.append('default-tab', config.defaultTab);
      if (config.theme === 'dark') codepenParams.append('theme-id', 'dark');
      if (config.editable) codepenParams.append('editable', 'true');
      return `https://codepen.io/team/codepen/embed/${config.embedId}?${codepenParams.toString()}`;
    }

    case 'codesandbox': {
      const sandboxParams = new URLSearchParams();
      if (config.theme) sandboxParams.append('theme', config.theme);
      if (config.editable === false) sandboxParams.append('editorsize', '0');
      return `https://codesandbox.io/embed/${config.embedId}?${sandboxParams.toString()}`;
    }

    case 'jsfiddle': {
      const jsfiddleParams = config.defaultTab || 'html,css,result';
      return `https://jsfiddle.net/${config.embedId}/embedded/${jsfiddleParams}/${config.theme || 'light'}/`;
    }

    case 'replit':
      return `https://replit.com/@${config.embedId}?embed=true`;

    default:
      return '';
  }
}

export function getAspectRatioStyle(ratio?: string): { paddingBottom: string } {
  switch (ratio) {
    case '16:9':
      return { paddingBottom: '56.25%' };
    case '4:3':
      return { paddingBottom: '75%' };
    case '1:1':
      return { paddingBottom: '100%' };
    case '9:16':
      return { paddingBottom: '177.78%' };
    default:
      return { paddingBottom: '56.25%' };
  }
}

export function getContainerStyle(
  containerStyle?: HtmlEmbedData['containerStyle']
): React.CSSProperties {
  const style: React.CSSProperties = {};

  if (containerStyle) {
    if (containerStyle.padding) style.padding = containerStyle.padding;
    if (containerStyle.margin) style.margin = containerStyle.margin;
    if (containerStyle.maxWidth) style.maxWidth = containerStyle.maxWidth;
    if (containerStyle.backgroundColor) style.backgroundColor = containerStyle.backgroundColor;
    if (containerStyle.borderRadius) style.borderRadius = containerStyle.borderRadius;
    if (containerStyle.boxShadow) style.boxShadow = containerStyle.boxShadow;
  }

  return style;
}
